import { Card, CardContent } from '@folio/components';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@folio/components/ui';
import {
  configControllerGetPublicTenantConfigQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  useMetabaseControllerGetIframeUrl,
} from 'korint-sdk';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Center } from '@folio/components/ui/flex';

declare global {
  interface Window {
    iFrameResize: (options: unknown, iframe: HTMLIFrameElement) => void;
  }
}

type MetabaseDashboardTab = {
  name: string;
  dashboardId: number;
  forTypes: string[];
};

function useIframeResizer(iframeRef: React.RefObject<HTMLIFrameElement | null>, iframeUrl: string) {
  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    if (!window.iFrameResize && iframeUrl) {
      script = document.createElement('script');
      script.src = iframeUrl.replace(/\/embed\/.*$/, '/app/iframeResizer.js');
      script.async = true;
      document.head.appendChild(script);
      script.onload = () => {
        if (iframeRef.current && window.iFrameResize) {
          window.iFrameResize({}, iframeRef.current);
        }
      };
    } else if (iframeRef.current && window.iFrameResize) {
      window.iFrameResize({}, iframeRef.current);
    }
    return () => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [iframeUrl, iframeRef]);
}

function MetabaseIframe({ iframeUrl }: { iframeUrl: string }) {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useIframeResizer(iframeRef, iframeUrl);

  return (
    <iframe
      ref={iframeRef}
      src={iframeUrl}
      title={t('analytics.iframeTitle')}
      allowTransparency
      className="w-full"
      onLoad={() => {
        if (iframeRef.current && window.iFrameResize) {
          window.iFrameResize({}, iframeRef.current);
        }
      }}
    />
  );
}

function MetabaseLoadingCard() {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <Center className="h-96">
          <div className="text-muted-foreground">{t('analytics.loading')}</div>
        </Center>
      </CardContent>
    </Card>
  );
}

function MetabaseEmptyCard() {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <Center className="h-96">
          <div className="text-muted-foreground">{t('analytics.empty')}</div>
        </Center>
      </CardContent>
    </Card>
  );
}

function MetabaseTabContent({ name }: { name: string }) {
  const { data, isLoading } = useMetabaseControllerGetIframeUrl({ name });

  if (isLoading) return <MetabaseLoadingCard />;
  if (!data?.iframeUrl) return <MetabaseEmptyCard />;

  return <MetabaseIframe iframeUrl={data.iframeUrl} />;
}

function MetabaseSingleDashboard() {
  const { data, isLoading } = useMetabaseControllerGetIframeUrl();

  if (isLoading) return <MetabaseLoadingCard />;
  if (!data?.iframeUrl) return <MetabaseEmptyCard />;

  return <MetabaseIframe iframeUrl={data.iframeUrl} />;
}

export function MetabaseAnalyticsModule() {
  const { t } = useTranslation();

  const { data: tabs, isPending: isTabsPending } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    select: (data): MetabaseDashboardTab[] | undefined => (data as any).metabaseDashboardIds,
  });

  // `forTypes` holds business role names, not firm types — see MetabaseDashboardTabConfig.
  const { data: businessRole, isPending: isBusinessRolePending } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    select: data => data.brokerRole,
  });

  if (isTabsPending || isBusinessRolePending) return <MetabaseLoadingCard />;

  const visibleTabs =
    tabs?.filter(tab => businessRole !== undefined && tab.forTypes.includes(businessRole)) ?? [];

  if (visibleTabs.length > 0) {
    return (
      <Tabs defaultValue={visibleTabs[0].name}>
        <TabsList className="relative flex w-full rounded-none bg-transparent h-auto p-0 border-b-2 border-gray-200">
          {visibleTabs.map(tab => (
            <TabsTrigger
              key={tab.name}
              value={tab.name}
              className="flex-auto -mb-0.5 rounded-none border-b-2 py-4 px-5 text-center text-sm font-medium shadow-none bg-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:border-gray-200 data-[state=inactive]:text-gray-500 hover:border-gray-300 hover:text-gray-700"
            >
              {t(`analytics.tabs.${tab.name}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {visibleTabs.map(tab => (
          <TabsContent key={tab.name} value={tab.name}>
            <MetabaseTabContent name={tab.name} />
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  return <MetabaseSingleDashboard />;
}
